import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Blade, Container, DataGridTable } from 'components'
import { claimsVehiclesColumns } from './claimsVehiclesTableData'
import { SafetyDetails, VehicleEvents } from 'interfaces/IClaimsSearching'
import { exportTableAsCSV, formatEventTypeLabel } from 'tools/helper'
import { getSafetyEventDetail } from 'api/claimsSearching'
import ClaimsSafetyDetails from '../claimsSafetyDetails'
import { GridRowHeightParams, useGridApiRef } from '@mui/x-data-grid'
import strings from 'strings'
import { isModalEld } from 'tools/customBehaviors'

const t = strings.TABLE_DEFAULTS
const e = strings.CSV_EXPORT
interface IClaimsVehiclesTableProps {
  carrierSelected: boolean
  selectedCarrierId: any
  selectedCarrierName: string
  vehiclesSafetyEventsList: VehicleEvents | null
  selectedEventId: number | null
  onEventSelect: (
    eventId: number | null,
    openDetails?: boolean,
    showRoute?: boolean
  ) => void
  onCarrierSelect: any
  onViewRoute: (
    vehicleId: string,
    eventTime: string,
    companyEldAuthenticationId: number
  ) => void
  openDetails: boolean
  useKilometers?: boolean
}

const ClaimsVehiclesTable: React.FC<IClaimsVehiclesTableProps> = ({
  carrierSelected,
  selectedCarrierId,
  selectedCarrierName,
  vehiclesSafetyEventsList,
  selectedEventId,
  onEventSelect,
  onCarrierSelect,
  onViewRoute,
  openDetails,
  useKilometers = false,
}) => {
  const [loading, setLoading] = useState(true)
  const [mappedVehicleSafetyEventsList, setMappedVehicleSafetyEventsList] =
    useState<any[]>([])
  const [safetyDetails, setSafetyDetails] =
    useState<SafetyDetails | undefined>()
  const [hasVideo, setHasVideo] = useState<boolean>(false)
  const [hasImage, setHasImage] = useState<boolean>(false)
  const [selectedEld, setSelectedEld] = useState<string>('')
  const detailRef = useRef<any>(null)
  const downloadCooldownRef = useRef(0)
  const apiRef = useGridApiRef()

  const mapVehicleSafetyEventsList = useCallback(() => {
    const mappedList = vehiclesSafetyEventsList?.safetyEvents
      ?.filter((event) => event?.latitude && event?.longitude)
      .map((event) => ({
        vehicleId: event?.id,
        carrierId: event?.carrierId,
        carrierName: getCarrierNameWithoutCode(
          event?.carrierName || selectedCarrierName
        ),
        companyEldAuthenticationId: event?.companyEldAuthenticationId,
        assetId: event?.vehicleId.assetEldId,
        vin: event?.vin,
        eld: event?.eldProvider,
        driverId: event?.driverId.driverEldId,
        driverName: event?.driverFirstName
          ? event?.driverFirstName +
            (event?.driverLastName
              ? ' ' + event?.driverLastName.charAt(0) + '.'
              : '')
          : event?.driverName
          ? event?.driverName
          : t.DASH_VALUE,
        event: formatEventTypeLabel(event?.type),
        eventTime: event?.time,
        onViewRoute: (rowData: any) => handleViewRoute(rowData),
        onViewDetails: (rowData: any) => handleViewDetails(rowData),
      }))
    setMappedVehicleSafetyEventsList(mappedList ?? [])
    setLoading(false)
  }, [vehiclesSafetyEventsList])

  useEffect(() => {
    if (vehiclesSafetyEventsList) {
      setLoading(true)
      mapVehicleSafetyEventsList()
    } else {
      setMappedVehicleSafetyEventsList([])
    }
  }, [vehiclesSafetyEventsList, mapVehicleSafetyEventsList])

  useEffect(() => {
    if (selectedEventId) {
      setLoading(true)
      callGetSafetyEventDetail()
    }
  }, [selectedEventId])

  const callGetSafetyEventDetail = () => {
    const selectedEvent = vehiclesSafetyEventsList?.safetyEvents?.find(
      (event) => event.id === selectedEventId
    )
    const selectedEld = selectedEvent?.eldProvider
    setSelectedEld(selectedEld ?? '')
    const carrierIdToUse = selectedEvent?.carrierId || selectedCarrierId
    getSafetyEventDetail(carrierIdToUse, selectedEventId, useKilometers ? { useKilometers: true } : undefined)
      .then((res: any) => {
        setSafetyDetails(res)
        const videoRows = res?.cameraData?.filter((c: any) => !!c.videoUrl) ?? []
        const toShow = videoRows.length > 0 ? videoRows : (res?.cameraData ?? [])
        setHasVideo(toShow.some((c: any) => !!c.videoUrl))
        setHasImage(!toShow.some((c: any) => !!c.videoUrl) && toShow.some((c: any) => !!c.imageUrl))
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const getEstimatedRowHeight = useCallback(
    ({ model, densityFactor }: GridRowHeightParams): number | null => {
      const baseHeight = t.DEFAULT_ROW_HEIGHT
      const actionCount = 2
      const heightPerProvider = t.DEFAULT_ELD_PROVIDER_ROW_HEIGHT
      const padding = t.DEFAULT_CELL_PADDING

      const calculatedHeight = actionCount * heightPerProvider + padding
      return Math.max(baseHeight, calculatedHeight) * densityFactor
    },
    []
  )

  const getCarrierNameWithoutCode = (carrierName: string) => {
    return carrierName.split('(')[0].trim()
  }

  const handleDownloadClick = () => {
    const currentTime = Date.now()
    const cooldownTime = 3000

    if ((!hasVideo && !hasImage) || !detailRef.current) {
      return
    }

    if (currentTime - downloadCooldownRef.current < cooldownTime) {
      return
    }

    downloadCooldownRef.current = currentTime
    detailRef?.current?.handleDownload()
  }

  const handleExport = () => {
    const csvString = apiRef.current.getDataAsCsv()
    const modifiedCsv = csvString
      .split('\n')
      .map((row) => row.split(',').slice(0, -1).join(','))
      .join('\n')
    exportTableAsCSV(modifiedCsv, e.CLAIMS_SEARCHING_CSV_EXPORT_NAME)
  }

  const handleViewRoute = (rowData: any) => {
    onCarrierSelect({
      carrierId: rowData.carrierId,
      carrierName: getCarrierNameWithoutCode(rowData.carrierName),
    })
    onEventSelect(rowData.vehicleId, false, true)
  }

  const handleViewDetails = (rowData: any) => {
    onCarrierSelect({
      carrierId: rowData.carrierId,
      carrierName: getCarrierNameWithoutCode(rowData.carrierName),
    })
    onEventSelect(rowData.vehicleId, true, false)
  }

  const tableMenuItems = [
    {
      label: e.DOWNLOAD_AS_CSV,
      key: 'export',
      action: () => {
        handleExport()
      },
    },
  ]

  return (
    <Container data-testid="claims-vehicles-table-section">
      <DataGridTable
        apiRef={apiRef}
        showTitle={false}
        loading={loading}
        data={mappedVehicleSafetyEventsList || []}
        columns={claimsVehiclesColumns}
        columnVisibilityModel={{
          vehicleId: false,
          carrierId: false,
          companyEldAuthenticationId: false,
        }}
        checkboxSelection={false}
        totalCount={mappedVehicleSafetyEventsList?.length}
        reduceHeight={300}
        multiSearchFilter={false}
        getRowId={(row: any) => row?.vehicleId}
        menuList={tableMenuItems}
        sortField={'eventTime'}
        sortOrder={'desc'}
        useDynamicRowHeight={true}
        getEstimatedRowHeight={getEstimatedRowHeight}
      />
      <Blade
        open={openDetails}
        title={'Details'}
        onClose={() => onEventSelect(null)}
        bodyNode={
          <ClaimsSafetyDetails
            selectedEvent={safetyDetails}
            selectedCarrierName={selectedCarrierName}
            selectedEld={selectedEld}
            loading={loading}
            ref={detailRef}
          />
        }
        size={'md'}
        footerBtns={{
          primary: {
            id: 'downloadSafetyDetailVideo',
            label: 'Download Media',
            onClick: () => handleDownloadClick(),
            disabled: (!hasVideo && !hasImage) || isModalEld(selectedEld),
          },
          secondary: {
            id: 'closeSafetyDetailVideo',
            label: 'Close',
            variant: 'outlined',
            onClick: () => onEventSelect(null),
          },
        }}
      />
    </Container>
  )
}

export default ClaimsVehiclesTable
